import FullPageLoading from 'components/FullPageLoading'
import NoData from 'components/NoData'
import _, { uniq } from 'lodash'
import React, {
    ReactNode, useCallback, useEffect, useState
} from 'react'
import { ReactSortable } from 'react-sortablejs'
import styled from 'styled-components'
import { RequestStatus } from 'types'
import { colors, sleep } from 'utils'
import DragIcon from '../Icon/DragIcon'

const S = {
    RowWrapper: styled.div<{ styledBackground: boolean }>`
        display: flex;
        align-items: center;
        background: ${({ styledBackground }) => (styledBackground ? `${colors.darkOpacity(0.05)}` : 'none')};
        margin-bottom: 3px;
    `,
}

export interface SortableItem {
    id: string | number
    order: number
    renderItem: (item?: any) => ReactNode
    renderBody: (item?: any) => ReactNode
}

interface SortableListProps {
    initialList: SortableItem[],
    updateOrder: (id?: string | number, order?: number, listId?: number) => void,
    disableSort?: boolean,
    onUpdateMinOrder?: (order: number) => void
    onUpdateMaxOrder?: (order: number) => void
    requestStatus?: RequestStatus
    styledBackground?: boolean
    group?: string
    groups?: any[]
    listId?: number
    handleAdd?:({ newIndex, item }) => void
    fetchData?: () => Promise<void>
}

const SortableList = ({
    initialList = [],
    updateOrder,
    disableSort,
    // MinOrder is used for creating new item if needed, the new item's order is equal (minOrder - 1000)
    // so the new item which has just been created will be on top of list. (It's optional)
    onUpdateMinOrder,
    onUpdateMaxOrder,
    requestStatus,
    styledBackground = true,
    group,
    groups,
    listId,
    fetchData= async () => {}
}: SortableListProps) => {
    const [list, setList] = useState(initialList)
    const [isLoading, setIsLoading] = useState(false)

    useEffect(() => {
        // Check the orders if they have duplicated value.
        // There are 2 cases which can be duplicated order:
        // 1. First load the items from old portal (all orders are 0)
        // 2. The order is set by (prevItemOrder + nextItemOrder)/2 and nextItemOrder is greater than prevItemOrder by 1 (rarely)

        const orders = list.map((e) => e.order)
        // Check duplicated orders
        const isDuplicated = uniq(orders).length !== orders.length
        if (isDuplicated) {
            // If duplicated, set default order (0, 1000, 2000, 3000, ....)
            list.forEach(async (item, index) => {
                await updateOrder(item.id, index*1000, listId)
            })
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [list])

    useEffect(() => {
        setList(initialList)
    }, [initialList])

    const handleSort = async ({ newIndex, pullMode, oldIndex }) => {
        if (disableSort) {
            setList(initialList)
            return
        }
        // Ignore sorting when item came from another list
        if (!pullMode) {
            try {
                setIsLoading(true)
                const movedItemId = list[oldIndex]?.id
                const prevItemOrder = list[newIndex > oldIndex ? newIndex : newIndex - 1]?.order
                const nextItemOrder = list[newIndex > oldIndex ? newIndex + 1 : newIndex]?.order

                if (newIndex === 0) {
                    await updateOrder(movedItemId, nextItemOrder - 1000, listId)
                    await onUpdateMinOrder?.(nextItemOrder - 1000)
                } else if (newIndex === list.length - 1) {
                    await updateOrder(movedItemId, prevItemOrder + 1000, listId)
                    await onUpdateMaxOrder?.(prevItemOrder + 1000)
                } else {
                    await updateOrder(
                        movedItemId,
                        Math.floor((prevItemOrder + nextItemOrder)/2) + 2,
                        listId
                    )
                }
                await sleep(750)
                await fetchData()
            } finally {
                setIsLoading(false)
            }
        }
    }

    const handleAdd = useCallback(async ({ newIndex, item }) => {
        try {
            setIsLoading(true)
            const tagName = item.innerText
            const tag = groups?.find((t) => t?.text === tagName)
            if (newIndex === 0) {
                const newOrder = _.isEmpty(list) ? 0 : list[0].order - 1000
                await updateOrder(tag.id, newOrder, listId)
                onUpdateMinOrder?.(newOrder - 1000)
            } else if (!_.isEmpty(list) && newIndex === list.length) {
                const newOrder = _.last(list).order + 1000
                await updateOrder(tag.id, newOrder, listId)
            } else {
                const newOrder = list[newIndex - 1].order + list[newIndex].order
                await updateOrder(tag.id, Math.floor(newOrder / 2) + 2, listId)
            }
            await sleep(500)
            await fetchData()
        } finally {
            setIsLoading(false)
        }
    }, [fetchData, groups, list, listId, onUpdateMinOrder, updateOrder])

    if (requestStatus === 'loading') {
        return <FullPageLoading />
    }

    return (
        <>
            <ReactSortable
                list={list}
                setList={setList}
                onSort={handleSort}
                onAdd={handleAdd}
                disabled={disableSort}
                sort={!disableSort}
                group={group}
                style={{minHeight: 24}}
            >
                {
                    list.map((item) => (
                        <div key={item.id}>
                            <S.RowWrapper styledBackground={styledBackground}>
                                <DragIcon disableSort={disableSort} />
                                {item.renderItem()}
                            </S.RowWrapper>
                            {item.renderBody()}
                        </div>
                    ))
                }
            </ReactSortable>
            {initialList.length === 0 && requestStatus === 'succeeded' && (
                <NoData />
            )}
            {isLoading && <FullPageLoading isAbsolute />}
        </>
    )
}

export default SortableList
